import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import '../assets/styles/Contact.scss';
import emailjs from '@emailjs/browser';
import TextField from '@mui/material/TextField';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiMail, FiMapPin, FiLinkedin, FiSend } from 'react-icons/fi';
import { trackContactFormStart, trackContactFormSubmit, trackSectionView } from '../utils/mixpanel';
import { SectionHeader } from './ui';

const EMAIL = 'hhamza1136@gmail.com';

function GlobeCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const NODE_COUNT   = 130;
    const CONNECT_DIST = 0.44;
    const ROTATE_SPEED = 0.0015;
    const MAX_SIGNALS = 6;
    const HOP_DURATION = 40; // frames per hop

    let animId: number;
    let W = 0, H = 0, rotY = 0;

    type P3 = { x: number; y: number; z: number };

    // Signal hopping node-to-node along actual edges
    type Signal = {
      from: number;  // current node
      to: number;    // next node
      t: number;     // 0..1 progress along this hop
      alpha: number; // brightness of this signal's edge
    };

    let pts: P3[] = [];
    let neighbors: number[][] = []; // adjacency list
    let signals: Signal[] = [];

    const isLight = () =>
      document.querySelector('.main-container')?.classList.contains('light-mode') ?? false;

    // Find connected neighbors for each node (within CONNECT_DIST after rotation — use static 3D dist)
    const buildNeighbors = () => {
      neighbors = pts.map(() => [] as number[]);
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const dz = pts[i].z - pts[j].z;
          const d = Math.sqrt(dx*dx + dy*dy + dz*dz);
          if (d < CONNECT_DIST) {
            neighbors[i].push(j);
            neighbors[j].push(i);
          }
        }
      }
    };

    const spawnSignal = () => {
      if (signals.length >= MAX_SIGNALS) return;
      const from = Math.floor(Math.random() * pts.length);
      const nb = neighbors[from];
      if (!nb || nb.length === 0) return;
      const to = nb[Math.floor(Math.random() * nb.length)];
      signals.push({ from, to, t: 0, alpha: 0.9 + Math.random() * 0.1 });
    };

    const build = () => {
      W = canvas.offsetWidth; H = canvas.offsetHeight;
      canvas.width = W; canvas.height = H;
      pts = [];
      signals = [];
      const golden = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < NODE_COUNT; i++) {
        const y = 1 - (i / (NODE_COUNT - 1)) * 2;
        const r = Math.sqrt(1 - y * y);
        const phi = golden * i;
        pts.push({ x: Math.cos(phi) * r, y, z: Math.sin(phi) * r });
      }
      buildNeighbors();
    };

    const project = (p: P3, cx: number, cy: number, radius: number) => {
      const ca = Math.cos(rotY), sa = Math.sin(rotY);
      const rx = p.x * ca - p.z * sa;
      const rz = p.x * sa + p.z * ca;
      const fov = 2.8, scale = fov / (fov + rz);
      return { sx: cx + rx * radius * scale, sy: cy + p.y * radius * scale, scale, rz };
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      rotY += ROTATE_SPEED;
      const light  = isLight();
      const cx = W / 2, cy = H / 2;
      const radius = Math.min(W, H) * 0.40;
      const proj = pts.map(p => project(p, cx, cy, radius));

      // ── 1. Edges ──────────────────────────────────────────────
      for (let i = 0; i < pts.length; i++) {
        if (proj[i].rz < -0.95) continue;
        for (let j = i + 1; j < pts.length; j++) {
          if (proj[j].rz < -0.95) continue;
          const ca = Math.cos(rotY), sa = Math.sin(rotY);
          const ax = pts[i].x*ca - pts[i].z*sa, bx = pts[j].x*ca - pts[j].z*sa;
          const dx = ax-bx, dy = pts[i].y-pts[j].y;
          const dz = (pts[i].x*sa+pts[i].z*ca)-(pts[j].x*sa+pts[j].z*ca);
          const dist = Math.sqrt(dx*dx+dy*dy+dz*dz);
          if (dist > CONNECT_DIST) continue;
          const avg   = (proj[i].scale + proj[j].scale) / 2;
          const alpha = (1 - dist / CONNECT_DIST) * avg * (light ? 0.35 : 0.55);
          ctx.beginPath();
          ctx.moveTo(proj[i].sx, proj[i].sy);
          ctx.lineTo(proj[j].sx, proj[j].sy);
          ctx.strokeStyle = light
            ? `rgba(99,102,241,${alpha.toFixed(3)})`
            : `rgba(180,190,255,${alpha.toFixed(3)})`;
          ctx.lineWidth = 0.9;
          ctx.stroke();
        }
      }


      // ── 3. Nodes ──────────────────────────────────────────────
      for (let i = 0; i < pts.length; i++) {
        if (proj[i].rz < -0.95) continue;
        const { sx, sy, scale } = proj[i];
        const alpha = scale * (light ? 0.7 : 0.9);
        ctx.beginPath();
        ctx.arc(sx, sy, scale * 3, 0, Math.PI * 2);
        ctx.fillStyle = light
          ? `rgba(99,102,241,${alpha.toFixed(2)})`
          : `rgba(220,225,255,${alpha.toFixed(2)})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    };

    build();
    // Kick off several transmissions immediately so globe is active from the start
    draw();
    const ro = new ResizeObserver(build);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(animId); ro.disconnect(); };
  }, []);

  return <canvas ref={canvasRef} className="contact-section__globe" />;
}

const contactInfo = [
  {
    icon: <FiMail />,
    label: 'Email',
    value: EMAIL,
    href: null,
    copyEmail: true,
  },
  {
    icon: <FiMapPin />,
    label: 'Location',
    value: 'Pakistan · Open to Remote & Relocation',
    href: null,
  },
  {
    icon: <FiLinkedin />,
    label: 'LinkedIn',
    value: 'ameer-hamza-39604214b',
    href: 'https://www.linkedin.com/in/ameer-hamza-39604214b/',
  },
];

function Contact() {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [nameError, setNameError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [messageError, setMessageError] = useState<boolean>(false);
  const [formStarted, setFormStarted] = useState(false);

  useEffect(() => {
    trackSectionView('contact');
  }, []);

  useEffect(() => {
    if ((name || email || message) && !formStarted) {
      trackContactFormStart();
      setFormStarted(true);
    }
  }, [name, email, message, formStarted]);

  const copyEmail = () => {
    navigator.clipboard.writeText(EMAIL).then(() => {
      toast.info('Email address copied to clipboard!', { autoClose: 3000 });
    });
  };

  const sendEmail = (e: any) => {
    e.preventDefault();
    setNameError(name === '');
    setEmailError(email === '');
    setMessageError(message === '');

    if (name !== '' && email !== '' && message !== '') {
      const templateParams = { name, email, message };
      emailjs.send('service_husrywz', 'template_m05qyrg', templateParams, 'CHODXPDyqrKy_7-v6').then(
        () => {
          toast.success('Thank you for reaching out! Your message has been successfully submitted.');
          trackContactFormSubmit(true);
          setName('');
          setEmail('');
          setMessage('');
        },
        (error) => {
          toast.error('Something went wrong. Please try again.');
          trackContactFormSubmit(false, error.text || 'Unknown error');
        }
      );
    }
  };

  return (
    <section className="contact-section" id="contact">
      <GlobeCanvas />
      <div className="contact-section__container">
        <div className="contact-section__grid">

          {/* Left Side */}
          <motion.div
            className="contact-section__left"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
          >
            <SectionHeader
              title="Contact Me"
              subtitle="Got a project waiting to be realized? Let's collaborate and make it happen!"
              align="left"
            />

            <p className="contact-section__tagline">
              I'm currently available for both full-time and freelance opportunities.
              Whether you have a project in mind or just want to connect — my inbox is always open.
            </p>

            <div className="contact-info">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.label}
                  className="contact-info__item"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.08 }}
                >
                  <div className="contact-info__icon">{item.icon}</div>
                  <div className="contact-info__body">
                    <span className="contact-info__label">{item.label}</span>
                    {(item as any).copyEmail ? (
                      <button onClick={copyEmail} className="contact-info__value contact-info__value--copy">
                        {item.value}
                      </button>
                    ) : item.href ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer" className="contact-info__value">
                        {item.value}
                      </a>
                    ) : (
                      <span className="contact-info__value contact-info__value--plain">{item.value}</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Side — Form */}
          <motion.div
            className="contact-section__right"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="contact-form-card">
              <div className="form-row">
                <TextField
                  required
                  fullWidth
                  label="Your Name"
                  placeholder="What's your name?"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={nameError}
                  helperText={nameError ? "Please enter your name" : ""}
                  variant="outlined"
                />
                <TextField
                  required
                  fullWidth
                  label="Email / Phone"
                  placeholder="How can I reach you?"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={emailError}
                  helperText={emailError ? "Please enter your email or phone" : ""}
                  variant="outlined"
                />
              </div>
              <TextField
                required
                fullWidth
                label="Message"
                placeholder="Tell me about your project or inquiry..."
                multiline
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                error={messageError}
                helperText={messageError ? "Please enter your message" : ""}
                variant="outlined"
              />
              <button className="contact-submit" onClick={sendEmail}>
                <FiSend />
                Send Message
              </button>
            </div>
          </motion.div>

        </div>
      </div>

      <ToastContainer
        position="bottom-right"
        autoClose={6000}
        hideProgressBar={true}
        closeOnClick
        pauseOnHover
        draggable
      />
    </section>
  );
}

export default Contact;
